import React, { SubmitEvent, useRef, useEffect, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Card } from "./UI";
import { useChat, useChatAuth } from "../../hooks/useChat";
import { createId } from "../../utils/createId";
import { TURNSTILE_SITE_KEY } from "../../config";

const chatSeed = [
  { id: createId(), role: "assistant", text: "Hola, soy el asistente de Velasco Software." },
  { id: createId(), role: "assistant", text: "Puedo ayudarte con AI Agents, MCPs, ChatBots y n8n." },
];

export const ChatPanel = ({
  className = "",
  compact = false,
  onClose,
}: {
  className?: string;
  compact?: boolean;
  onClose?: () => void;
}) => {
  const height = compact ? "h-48" : "h-72";

  const [messages, setMessages] = React.useState(chatSeed);
  const [input, setInput] = React.useState("");
  const [isCanSend, setIsCanSend] = React.useState<boolean>(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isRespondingLocal, setIsRespondingLocal] = useState(false);

  const { mutateAsync: mutateAsyncChat, isPending: isPendingChat } = useChat();
  const { mutateAsync: mutateAsyncChatAuth, isPending: isPendingChatAuth } = useChatAuth();
  
  const turnstileRef = useRef<any | null>(null);
  const [TurnstileComp, setTurnstileComp] = useState<any | null>(null);
  const canSend = ((!TurnstileComp) || isCanSend) && !isPendingChat && !isPendingChatAuth;

  const messagesRef = useRef<HTMLDivElement | null>(null);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let mounted = true;
    import('@marsidev/react-turnstile')
      .then((m) => { if (mounted) setTurnstileComp(() => m.Turnstile); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (isUserScrolling) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } catch (e) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, isRespondingLocal, isPendingChat, isUserScrolling]);

  const handleScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsUserScrolling(distanceFromBottom > 50);
  };

  const authorizeChats = async (token: string) => {
    const response = await mutateAsyncChatAuth({ token });

    if(response.success) {
      sessionStorage.setItem("chat_token", response.access_token);
      setIsCanSend(true);
    }
  }

  const sendMessage = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = input.trim();
    if (!query) {
      return;
    }

    setMessages((prev) => [...prev, { id: createId(), role: "user", text: query }]);
    setInput("");
    setIsRespondingLocal(true);

    try {
      const response: any = await mutateAsyncChat({ query });

      let content = response?.text ?? response?.content ?? (typeof response === 'string' ? response : undefined);

      if (typeof content === 'string' && content.includes('[object Object]')) {
        content = `Respuesta: ${query}`;
      }

      setMessages((prev) => [
        ...prev,
        { id: createId(), role: 'assistant', text: content ?? 'Respuesta recibida' },
      ]);

    } catch (error: any) {
      console.error(error);
      
      const errCodes = error?.response?.data?.['error-codes'] || error?.response?.data?.errorCodes || null;
      if (Array.isArray(errCodes) && errCodes.includes('timeout-or-duplicate')) {
        try {
          turnstileRef.current?.reset?.();
        } catch (e) {
          console.warn('Failed to reset Turnstile after captcha error', e);
        }
      }
    } finally {
      setIsRespondingLocal(false);
    }
  };

  return (
    <Card className={`bg-[#151a28]/70 ${className}`}>
      {/* scrollbar + typing CSS injected locally so styles are present without touching global css */}
      <style>{`
        .chat-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .chat-scroll::-webkit-scrollbar-track { background: transparent; }
        .chat-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 8px; }
        .chat-scroll:hover::-webkit-scrollbar-thumb, .chat-scroll.user-scrolled::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.6); }
        .typing-dots { display:inline-block; }
        .typing-dots span { display:inline-block; width:6px; height:6px; margin:0 2px; background:rgba(255,255,255,0.8); border-radius:50%; opacity:0.2; animation: typing 1s infinite; }
        .typing-dots span:nth-child(1){ animation-delay:0s }
        .typing-dots span:nth-child(2){ animation-delay:0.15s }
        .typing-dots span:nth-child(3){ animation-delay:0.3s }
        @keyframes typing { 0% { transform: translateY(0); opacity:0.2 } 50% { transform: translateY(-4px); opacity:1 } 100% { transform: translateY(0); opacity:0.2 } }
      `}</style>

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-100 font-semibold">
            <MessageCircle className="w-4 h-4 text-indigo-300" />
            Chat de atencion
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-xs text-slate-300 hover:text-white px-2 py-1 rounded-lg border border-white/10 hover:bg-white/5"
          >
            Cerrar
          </button>
        )}
      </div>

      <div
        ref={messagesRef}
        onScroll={handleScroll}
        className={`mt-4 space-y-3 overflow-y-auto pr-2 ${height} chat-scroll ${isUserScrolling ? 'user-scrolled' : ''}`}
      >
        {messages.map((message) => (
          <ChatBubble key={message.id} role={message.role as "assistant" | "user"} text={message.text} />
        ))}

        {/* typing indicator while waiting for the assistant */}
        {(isPendingChat || isRespondingLocal) && (
          <div className="flex justify-start">
            <div className="max-w-[60%] rounded-2xl px-3 py-2 bg-white/5 border border-white/10 text-slate-200 text-sm">
              <span className="typing-dots"><span></span><span></span><span></span></span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={sendMessage} className="mt-4 flex flex-col gap-3">
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-100">
          La AI puede cometer errores. Revisá la información antes de tomar decisiones importantes.
        </div>
        <div className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-[#0f1322]/70 p-3">
          <div className="flex items-center gap-2">
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Escribi tu mensaje..."
              className="flex-1 rounded-xl border border-white/10 bg-[#0f1322] px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            />
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Enviar mensaje"
              className="inline-flex items-center justify-center rounded-xl bg-indigo-500/80 px-3 py-2 text-sm text-white transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex justify-end overflow-hidden rounded-xl border border-white/10 bg-[#0f1322] p-2">
              {TurnstileComp ? (
                <TurnstileComp
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token: string) => authorizeChats(token)}
                  onExpire={() => null}
                  onError={() => null}
                />
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </Card>
  );
};

const formatInline = (part: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /\*\*(.+?)\*\*/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(part)) !== null) {
    if (match.index > lastIndex) nodes.push(part.slice(lastIndex, match.index));
    nodes.push(<strong key={nodes.length}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < part.length) nodes.push(part.slice(lastIndex));
  return nodes;
};

const renderFormattedText = (text: string) => {
  return text.split(/\n\n+/).map((para, i) => (
    <p key={i} className="m-0 text-sm">
      {para.split(/\n/).reduce<React.ReactNode[]>((acc, line, idx) => {
        if (idx > 0) acc.push(<br key={`br${idx}`} />);
        acc.push(...formatInline(line));
        return acc;
      }, [])}
    </p>
  ));
};

const ChatBubble = ({ role, text }: { role: "assistant" | "user"; text: string }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[80%] rounded-2xl px-3 py-2 ${role === "user"
        ? "bg-indigo-500/20 border border-indigo-400/30 text-slate-100 text-sm"
        : "bg-white/5 border border-white/10 text-slate-200 text-sm"
        }`}
    >
      {renderFormattedText(text)}
    </div>
  </div>
);

export const FloatingChat = () => {
  const location = (useLocation() as any) ?? { pathname: "" };
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (location.pathname === "/contacto") setOpen(false);
  }, [location.pathname]);

  if (location.pathname === "/contacto") return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <ChatPanel
          compact
          onClose={() => setOpen(false)}
          className="w-[20rem] sm:w-[22rem] shadow-2xl"
        />
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full px-4 py-3 bg-indigo-500/90 hover:bg-indigo-500 text-white shadow-lg border border-white/10"
        aria-expanded={open}
        aria-label="Abrir chat de atencion"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-semibold">{open ? "Cerrar chat" : "Chat en vivo"}</span>
      </button>
    </div>
  );
};
